import React, { useState } from "react"
import { useMutation } from "urql"

import { AnimatedModal } from "../../../components/shared/Modal"
import { useToast } from "../../../contexts/ToastContext"

import { COMPLETE_CALENDAR_RECONNECT_MIGRATION_MUTATION } from "./graphql"
import { providerLabel } from "./selectors"
import { CalendarConnectionSummary, CompleteCalendarReconnectMigrationPayload } from "./types"

interface Props {
  connection: CalendarConnectionSummary | null
  onClose: () => void
  onCompleted: (summary: string) => void
}

// Finish guided Cronofy → native reconnect: unplug legacy sync, push Heal.me
// bookings, remove duplicate Google booking clones.
const CompleteReconnectMigrationModal = ({ connection, onClose, onCompleted }: Props) => {
  const [submitting, setSubmitting] = useState(false)
  const [, completeReconnect] = useMutation<{
    completeCalendarReconnectMigration: CompleteCalendarReconnectMigrationPayload
  }>(COMPLETE_CALENDAR_RECONNECT_MIGRATION_MUTATION)
  const { showToast } = useToast()

  if (!connection) return null

  const complete = () => {
    setSubmitting(true)
    completeReconnect({
      connectionPublicId: connection.publicId,
      enableOrphanDeletes: true
    }).then((result) => {
      setSubmitting(false)
      const payload = result.data?.completeCalendarReconnectMigration
      if (payload?.result === "success") {
        const deleted = payload.deleted || 0
        const wouldDelete = payload.wouldDelete || 0
        const summary =
          deleted > 0
            ? `Cleanup complete — removed ${deleted} duplicate Google booking${deleted === 1 ? "" : "s"}`
            : wouldDelete > 0
              ? `Cleanup finished — ${wouldDelete} duplicate${wouldDelete === 1 ? "" : "s"} listed (delete flag off)`
              : "Cleanup complete — old sync removed and bookings refreshed"
        showToast(summary)
        onCompleted(summary)
      } else {
        showToast({ content: payload?.errors?.[0] || "Could not finish calendar cleanup", type: "error" })
      }
    })
  }

  return (
    <AnimatedModal
      header="Finish switching to Heal.me sync"
      visible={Boolean(connection)}
      hideModal={onClose}
      showFooter={true}
      actionButtonCopy={submitting ? "Cleaning up…" : "Yes, finish cleanup"}
      actionButtonType="primary"
      saveDisabled={submitting}
      onSave={complete}>
      <p>
        Turn off the old {providerLabel(connection.provider)} sync
        {connection.displayIdentity ? ` (${connection.displayIdentity})` : ""}, push your Heal.me bookings to the new
        connection, and remove duplicate Google booking copies left from the old sync. Personal Google events are not
        removed. Heal.me appointments are not deleted.
      </p>
    </AnimatedModal>
  )
}

export default CompleteReconnectMigrationModal
